import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import MentionInput from '@/components/MentionInput';
import { convertMentionsForStorage, renderMentions, getUniqueMentions } from '@/lib/mentionUtils';
import { sendNotification } from '@/lib/notifications';
import { Send, MessageSquare, Paperclip, Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

/**
 * WorkflowRunComments Component
 * Comment section for workflow runs with @mentions support
 */
const WorkflowRunComments = ({
  workflowRunId,
  workflowName,
  projectId,
  className = ''
}) => {
  const { userProfile } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [mentionedUserIds, setMentionedUserIds] = useState([]);
  const [mentionMap, setMentionMap] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const mentionInputRef = useRef(null);

  // Fetch comments
  useEffect(() => {
    if (workflowRunId) {
      fetchComments();
      setupRealtimeSubscription();
    }
  }, [workflowRunId]);

  const setupRealtimeSubscription = async () => {
    // Set auth token for realtime
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      await supabase.realtime.setAuth(session.access_token);
    }

    const channel = supabase
      .channel(`workflow-run-comments-${workflowRunId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'workflow_run_comments',
          filter: `workflow_run_id=eq.${workflowRunId}`,
        },
        async (payload) => {
          if (payload.eventType === 'INSERT') {
            // Check if we already have this comment (from optimistic update)
            setComments(prev => {
              if (prev.find(c => c.id === payload.new.id)) {
                return prev; // Already exists, skip
              }
              // Fetch the full comment with user profile
              supabase
                .from('workflow_run_comments')
                .select(`*, user_profiles(id, first_name, last_name, display_name, avatar_url, email)`)
                .eq('id', payload.new.id)
                .single()
                .then(({ data }) => {
                  if (data) {
                    setComments(p => {
                      if (p.find(c => c.id === data.id)) return p;
                      return [...p, data];
                    });
                  }
                });
              return prev;
            });
          } else if (payload.eventType === 'DELETE') {
            setComments(prev => prev.filter(c => c.id !== payload.old.id));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const fetchComments = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('workflow_run_comments')
        .select(`
          *,
          user_profiles (
            id,
            first_name,
            last_name,
            display_name,
            avatar_url,
            email
          )
        `)
        .eq('workflow_run_id', workflowRunId)
        .order('created_date', { ascending: true });

      if (error) throw error;
      setComments(data || []);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!newComment.trim() || !userProfile?.id) return;

    try {
      setSubmitting(true);

      // Convert mentions to storage format
      const commentWithUUIDs = convertMentionsForStorage(newComment.trim(), mentionMap);
      const finalMentionedUserIds = getUniqueMentions(commentWithUUIDs);

      // Save comment
      const { data: insertedComment, error } = await supabase
        .from('workflow_run_comments')
        .insert({
          workflow_run_id: workflowRunId,
          user_id: userProfile.id,
          comment: commentWithUUIDs
        })
        .select(`
          *,
          user_profiles (
            id,
            first_name,
            last_name,
            display_name,
            avatar_url,
            email
          )
        `)
        .single();

      if (error) throw error;

      // Add to local state immediately
      setComments(prev => [...prev, insertedComment]);

      // Send notifications to mentioned users
      if (finalMentionedUserIds.length > 0) {
        const usersToNotify = finalMentionedUserIds.filter(id => id !== userProfile.id);

        if (usersToNotify.length > 0) {
          await sendNotification({
            notification_type: 'mentions',
            user_ids: usersToNotify,
            title: 'You were mentioned',
            message: `${userProfile.display_name || userProfile.first_name} mentioned you in a comment on workflow: ${workflowName}`,
            link_url: `/space/${projectId}/workflow-run/${workflowRunId}`,
            entity_type: 'workflow_run_comment',
            entity_id: insertedComment.id,
            actor_id: userProfile.id,
            space_id: projectId || null,
            group_key: `workflow_run_${workflowRunId}_mentions`
          });
        }
      }

      // Clear input
      setNewComment('');
      setMentionedUserIds([]);
      setMentionMap({});
      if (mentionInputRef.current) {
        mentionInputRef.current.clear();
      }

      toast.success('Comment added');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const getUserInitials = (user) => {
    if (!user) return '?';
    return `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase() || '?';
  };

  const getUserDisplayName = (user) => {
    if (!user) return 'Unknown User';
    return user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown User';
  };

  return (
    <div className={`bg-card border rounded-lg ${className}`}>
      {/* Header */}
      <div className="px-6 py-4 border-b">
        <h3 className="font-semibold flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          Comments
          {comments.length > 0 && (
            <span className="text-sm font-normal text-muted-foreground">
              ({comments.length})
            </span>
          )}
        </h3>
      </div>

      {/* Comments List */}
      <div className="px-6 py-4 space-y-4 max-h-[400px] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Loading comments...
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No comments yet. Start the conversation!</p>
          </div>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className="flex gap-3">
              <Avatar className="h-8 w-8 flex-shrink-0">
                <AvatarImage src={comment.user_profiles?.avatar_url} />
                <AvatarFallback className="text-xs">
                  {getUserInitials(comment.user_profiles)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm">
                    {getUserDisplayName(comment.user_profiles)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(comment.created_date), { addSuffix: true })}
                  </span>
                </div>
                <p className="text-sm text-foreground mt-1 whitespace-pre-wrap break-words">
                  {renderMentions(comment.comment).map((part, idx) => {
                    if (typeof part === 'string') {
                      return <span key={idx}>{part}</span>;
                    } else if (part.type === 'mention') {
                      return (
                        <span
                          key={part.key}
                          className="font-medium text-blue-600 bg-blue-50 px-1 rounded"
                        >
                          @{part.displayName}
                        </span>
                      );
                    }
                    return null;
                  })}
                </p>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Comment Form */}
      <div className="px-6 py-4 border-t bg-muted/30">
        <div className="flex gap-3">
          <Avatar className="h-10 w-10 flex-shrink-0">
            <AvatarImage src={userProfile?.avatar_url} />
            <AvatarFallback>
              {getUserInitials(userProfile)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <MentionInput
              ref={mentionInputRef}
              value={newComment}
              onChange={(text, mentionedIds, mentionMapData) => {
                setNewComment(text);
                setMentionedUserIds(mentionedIds);
                setMentionMap(mentionMapData);
              }}
              placeholder="Write a comment... Type @ to mention other users."
              rows={3}
              disabled={submitting}
              onKeyDown={(e) => {
                if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
            />
            <div className="flex items-center justify-end gap-2 mt-2">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground"
                disabled={submitting}
              >
                <Paperclip className="h-4 w-4" />
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || !newComment.trim()}
                size="sm"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowRunComments;
